import { useEffect, useMemo, useState } from "react";
import { getAiUsage, setAiUsagePlans, type AiUsageReport, type UsageNullableSplit, type UsagePlanSetting, type UsagePlanTool, type UsageTimeline, type UsageTimelineBucket, type UsageTimelineGranularity, type UsageTokenSplit } from "../api/admin";
import { IconBack, IconX } from "./admin/icons";
import "./admin/members.css";
import "./admin/usage.css";

/** `onOpenErrors` is required so a new render site cannot compile a dead tab. */
type Props = { onClose: () => void; onOpenUsers: () => void; onOpenErrors: () => void };
type ToolFilter = "all" | "claude" | "codex";

function tokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(value >= 10_000_000_000 ? 1 : 2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 1 : 2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 1 : 2)}K`;
  return value.toLocaleString();
}

function usd(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: value % 1 ? 2 : 0 }).format(value);
}

function monthName(month: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`));
}

function shortWeek(day: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${day}T00:00:00Z`));
}

function ago(value: string | null): string {
  if (!value) return "No activity";
  const hours = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 3_600_000));
  if (hours < 1) return "Active now";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function filtered(split: UsageTokenSplit, tool: ToolFilter): number {
  return tool === "all" ? split.total : split[tool];
}

function filteredNullable(split: UsageNullableSplit, tool: ToolFilter): number | null {
  return tool === "all" ? split.total : split[tool];
}

function toolLabel(tool: ToolFilter): string {
  return tool === "all" ? "All tools" : tool === "claude" ? "Claude Code" : "Codex";
}

function Trend({ value }: { value: number | null }) {
  if (value === null) return <span className="usage-trend neutral">New baseline</span>;
  const direction = value > 0 ? "up" : value < 0 ? "down" : "neutral";
  return <span className={`usage-trend ${direction}`}>{value > 0 ? "+" : ""}{value}% pace</span>;
}

type UsageSubscription = AiUsageReport["subscriptions"][number];
type UsagePlan = UsagePlanSetting["plans"][number];

/**
 * What the badge under a plan says.
 *
 * "No source" used to cover both a slot nobody configured and a slot whose
 * token the provider rejected, because an errored reading has no percentage and
 * fell into the same branch as no reading at all. A detail is the server saying
 * a source answered and answered badly, so it is the one case that reads Error.
 */
function planState(plan: UsagePlan): string {
  if (plan.source === "automatic") return plan.duplicate ? "Duplicate" : plan.stale ? "Stale" : "Auto";
  if (plan.source === "manual") return "Fallback";
  return plan.detail ? "Error" : "No source";
}

function planTitle(plan: UsagePlan): string | undefined {
  const reset = plan.resetAt ? `Resets ${new Date(plan.resetAt).toLocaleString()}` : null;
  const duplicate = plan.duplicate ? "Same provider account as an earlier plan, so it is not counted in the average" : null;
  const parts = [plan.detail ?? null, duplicate, reset].filter((part): part is string => part !== null);
  return parts.length ? parts.join(" · ") : undefined;
}

/** Stated once, and the same map the server keeps, so a third provider cannot
 *  fall through to Codex and have its plan edits saved under Codex's key. */
const SUBSCRIPTION_PLAN_TOOLS: Record<string, UsagePlanTool | undefined> = { "claude-code": "claude", chatgpt: "codex" };

function SubscriptionCard({ subscription, onSaved }: { subscription: UsageSubscription; onSaved: (setting: UsagePlanSetting) => void }) {
  const tool: UsagePlanTool = SUBSCRIPTION_PLAN_TOOLS[subscription.id] ?? "claude";
  const utilization = subscription.weeklyUtilization ?? {
    tool,
    planCount: subscription.seats,
    weeklyPercentages: Array.from({ length: subscription.seats }, () => null),
    manualPercentages: Array.from({ length: subscription.seats }, () => null),
    plans: Array.from({ length: subscription.seats }, () => ({ percent: null, source: "unavailable" as const, observedAt: null, resetAt: null, stale: false, detail: null, duplicate: false })),
    averagePercent: null,
    reportedPlans: 0,
    automaticPlans: 0,
    manualPlans: 0,
    duplicatePlans: 0,
    unmappedReadings: 0,
    updatedAt: null,
    refreshedAt: null,
  };
  const manualPercentages = utilization.manualPercentages ?? utilization.weeklyPercentages;
  const plans: UsagePlan[] = utilization.plans ?? utilization.weeklyPercentages.map((percent) => ({
    percent,
    source: percent === null ? "unavailable" as const : "manual" as const,
    observedAt: utilization.updatedAt,
    resetAt: null,
    stale: false,
    detail: null,
    duplicate: false,
  }));
  const automaticPlans = utilization.automaticPlans ?? plans.filter((plan) => plan.source === "automatic").length;
  const manualPlans = utilization.manualPlans ?? plans.filter((plan) => plan.source === "manual").length;
  const unavailablePlans = Math.max(0, utilization.planCount - automaticPlans - manualPlans);
  const duplicatePlans = utilization.duplicatePlans ?? plans.filter((plan) => plan.duplicate).length;
  // More connected sources than plan slots. Their readings are fetched and then
  // shown nowhere, so the count is the only sign the card is incomplete.
  const unmappedReadings = utilization.unmappedReadings ?? 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Array<number | null>>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const beginEditing = () => {
    setDraft([...manualPercentages]);
    setSaveError(null);
    setEditing(true);
  };
  const resizePlans = (count: number) => {
    const safe = Math.max(1, Math.min(20, Math.round(count) || 1));
    setDraft((current) => Array.from({ length: safe }, (_, index) => current[index] ?? null));
  };
  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = await setAiUsagePlans(tool, draft);
      onSaved(next);
      setEditing(false);
    } catch (error) {
      setSaveError((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className={`usage-sub-card ${subscription.id}`}>
      <div className="usage-sub-top">
        <span className={`usage-provider-mark ${subscription.id}`} aria-hidden="true" />
        <div>
          <span className="usage-eyebrow">{subscription.provider}</span>
          <h3>{subscription.product}</h3>
        </div>
        <span className={`usage-source-state${subscription.connected && subscription.complete ? " connected" : ""}`}>
          {!subscription.connected ? "Not connected" : subscription.complete ? "Connected" : "Partial data"}
        </span>
      </div>
      <div className="usage-sub-cost"><strong>{usd(subscription.monthlyCostUsd)}</strong><span>/ month · {subscription.seats} plan{subscription.seats === 1 ? "" : "s"}</span></div>
      <div className="usage-capacity">
        <div className="usage-capacity-head">
          <span>Weekly plan average <small>{automaticPlans > 0 ? "automatic" : "manual fallback"}</small></span>
          <strong>{utilization.averagePercent === null ? "—" : `${utilization.averagePercent}%`}</strong>
        </div>
        <div className="usage-capacity-track" aria-label={`${subscription.tool} weekly plan average`}>
          <span style={{ width: `${utilization.averagePercent ?? 0}%` }} />
        </div>
        <div className="usage-capacity-plans" aria-label={`${subscription.tool} individual weekly plan utilization`}>
          {plans.map((plan, index) => (
            <span className={`${plan.source}${plan.stale ? " stale" : ""}${plan.duplicate ? " duplicate" : ""}${planState(plan) === "Error" ? " errored" : ""}`} key={index} title={planTitle(plan)}>
              P{index + 1} <b>{plan.percent === null ? "—" : `${plan.percent}%`}</b><small>{planState(plan)}</small>
            </span>
          ))}
        </div>
        <div className="usage-capacity-meta">
          <span>{automaticPlans} automatic · {manualPlans} fallback{unavailablePlans ? ` · ${unavailablePlans} unavailable` : ""}{duplicatePlans ? ` · ${duplicatePlans} duplicate` : ""}{unmappedReadings ? ` · ${unmappedReadings} source${unmappedReadings === 1 ? "" : "s"} with no plan` : ""}{utilization.refreshedAt ? ` · synced ${ago(utilization.refreshedAt)}` : ""}</span>
          <button type="button" onClick={beginEditing}>Edit plans</button>
        </div>
      </div>
      {editing && (
        <div className="usage-plan-editor">
          <label className="usage-plan-count">Number of plans<input aria-label={`${subscription.tool} number of plans`} type="number" min="1" max="20" value={draft.length} onChange={(event) => resizePlans(Number(event.currentTarget.value))} /></label>
          <small className="usage-plan-help">These values are used only when an automatic plan reading is unavailable.</small>
          <div className="usage-plan-fields">
            {draft.map((value, index) => (
              <label key={index}>Plan {index + 1}<span><input aria-label={`${subscription.tool} plan ${index + 1} weekly usage`} type="number" min="0" max="100" step="0.1" placeholder="—" value={value ?? ""} onChange={(event) => {
                const raw = event.currentTarget.value;
                setDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? (raw === "" ? null : Number(raw)) : entry));
              }} />%</span></label>
            ))}
          </div>
          {saveError && <p role="alert">{saveError}</p>}
          <div className="usage-plan-actions"><button type="button" onClick={() => setEditing(false)} disabled={saving}>Cancel</button><button type="button" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save usage"}</button></div>
        </div>
      )}
      <div className="usage-sub-facts">
        <div><span>This week</span><b>{tokens(subscription.currentWeekTokens)}</b></div>
        <div><span>30 days</span><b>{tokens(subscription.last30DaysTokens)}</b></div>
        <div><span>Next month</span><b>{tokens(subscription.projectedNextMonthTokens)}</b></div>
      </div>
      <footer>{subscription.tool} · {ago(subscription.lastActivityAt)} · {subscription.filesScanned.toLocaleString()}{subscription.complete ? "" : ` of ${subscription.filesDiscovered.toLocaleString()}`} history files scanned</footer>
    </article>
  );
}

function UsageTrendChart({ report, filter }: { report: AiUsageReport; filter: ToolFilter }) {
  const max = Math.max(1, ...report.weeks.map((week) => filtered(week, filter)));
  return (
    <section className="usage-panel usage-chart-panel" aria-labelledby="usage-trend-title">
      <div className="usage-panel-head">
        <div>
          <span className="usage-eyebrow">Eight-week signal · {toolLabel(filter)}</span>
          <h3 id="usage-trend-title">Activity by coding tool</h3>
        </div>
        <div className="usage-legend" aria-label="Chart legend">
          {(filter === "all" || filter === "claude") && <span><i className="claude" />Claude Code</span>}
          {(filter === "all" || filter === "codex") && <span><i className="codex" />Codex</span>}
        </div>
      </div>
      <div className="usage-chart" role="img" aria-label="Weekly token activity split between Claude Code and Codex">
        {report.weeks.map((week) => {
          const visible = filtered(week, filter);
          const height = Math.max(2, (visible / max) * 100);
          const claudeShare = week.total ? (week.claude / week.total) * 100 : 0;
          return (
            <div className={`usage-week${week.current ? " current" : ""}`} key={week.start}>
              <span className="usage-week-total">{visible ? tokens(visible) : "—"}</span>
              <div className="usage-bar-rail">
                <div className="usage-bar" style={{ height: `${height}%` }} title={`${tokens(visible)} ${toolLabel(filter)} tokens`}>
                  {(filter === "all" || filter === "claude") && <span className="usage-bar-claude" style={{ height: `${filter === "claude" ? 100 : claudeShare}%` }} />}
                  {(filter === "all" || filter === "codex") && <span className="usage-bar-codex" style={{ height: `${filter === "codex" ? 100 : 100 - claudeShare}%` }} />}
                </div>
              </div>
              <span className="usage-week-label">{shortWeek(week.start)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function usdPrecise(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(value);
}

const GRANULARITY_ORDER: UsageTimelineGranularity[] = ["week", "month"];

/**
 * One period's money, by project.
 *
 * `Not attributed` is its own row rather than a silent shortfall: a fee owed for
 * a tool nobody used is real spending, and spreading it over the projects that
 * did work would charge Claude's users for Codex.
 */
function BucketBreakdown({
  bucket,
  filter,
  projectName,
}: {
  bucket: UsageTimelineBucket;
  filter: ToolFilter;
  projectName: (id: string) => string;
}) {
  const rows = (bucket.projects ?? []).filter((project) => filtered(project.costUsd, filter) > 0
    || filtered(project.tokens, filter) > 0);
  const unattributed = bucket.unattributedCostUsd ? filtered(bucket.unattributedCostUsd, filter) : 0;
  const period = `${shortWeek(bucket.start)} to ${shortWeek(bucket.end)}`;
  return (
    <div className="usage-expense-detail">
      <div className="usage-panel-head usage-expense-detail-head">
        <div>
          <span className="usage-eyebrow">{period}{bucket.partial ? " · still running" : ""}</span>
          <h4>Where {usdPrecise(filtered(bucket.costUsd, filter))} went</h4>
        </div>
        <span className="usage-expense-detail-meta">
          {tokens(filtered(bucket.tokens, filter))} tokens over {bucket.days} day{bucket.days === 1 ? "" : "s"}
        </span>
      </div>
      {rows.length === 0 && unattributed <= 0 ? (
        <p className="usage-expense-empty">No {toolLabel(filter).toLowerCase()} activity was recorded in this period.</p>
      ) : (
        <div className="usage-table-wrap">
          <table className="usage-table usage-expense-table">
            <thead><tr><th>Project</th><th>Cost</th><th>Share</th><th>Tokens</th></tr></thead>
            <tbody>
              {rows.map((project) => (
                <tr key={project.id}>
                  <td>
                    <div className="usage-project-name">
                      <span className="usage-project-glyph">{projectName(project.id).slice(0, 2).toUpperCase()}</span>
                      <span><b>{projectName(project.id)}</b></span>
                    </div>
                  </td>
                  <td><b className="usage-number">{usdPrecise(filtered(project.costUsd, filter))}</b>{filter === "all" && (project.costUsd.claude > 0 || project.costUsd.codex > 0) ? <div className="usage-split"><span className="claude">{usdPrecise(project.costUsd.claude)}</span><span className="codex">{usdPrecise(project.costUsd.codex)}</span></div> : null}</td>
                  <td><b className="usage-number">{project.sharePercent.toFixed(1)}%</b></td>
                  <td><b className="usage-number">{tokens(filtered(project.tokens, filter))}</b></td>
                </tr>
              ))}
              {unattributed > 0 && (
                <tr className="usage-expense-unattributed">
                  <td>
                    <div className="usage-project-name">
                      <span className="usage-project-glyph local">--</span>
                      <span><b>Not attributed</b><small>fee for a tool nobody used this period</small></span>
                    </div>
                  </td>
                  <td><b className="usage-number">{usdPrecise(unattributed)}</b></td>
                  <td><b className="usage-number">--</b></td>
                  <td><b className="usage-number">0</b></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * What the workspace has actually been paying, period by period.
 *
 * These subscriptions are not metered, so the fee is owed on quiet weeks too and
 * the bar heights are close to flat by design. The number that moves is cost per
 * million tokens, which is why it is on every bar rather than buried in a
 * tooltip: a period that cost the same as the last one but produced half the
 * work is the thing this panel exists to make visible.
 *
 * Selecting a bar opens that period's project breakdown. The latest period is
 * selected on arrival, because a panel that answers nothing until it is clicked
 * teaches nobody that it can be clicked.
 */
function UsageExpenseTimeline({
  timelines,
  filter,
  projectName,
}: {
  timelines: NonNullable<AiUsageReport["timeline"]>;
  filter: ToolFilter;
  projectName: (id: string) => string;
}) {
  const [granularity, setGranularity] = useState<UsageTimelineGranularity>("week");
  const [picked, setPicked] = useState<string | null>(null);
  const available = GRANULARITY_ORDER.filter((value) => timelines[value]);
  const timeline: UsageTimeline | undefined = timelines[granularity] ?? (available[0] ? timelines[available[0]] : undefined);
  if (!timeline) return null;

  const buckets = timeline.buckets;
  // The pick is stored per granularity so switching Weekly/Monthly falls back to
  // that view's latest period instead of resolving to a start date it has no
  // bucket for.
  const pickedStart = picked?.startsWith(`${timeline.granularity}:`) ? picked.slice(timeline.granularity.length + 1) : null;
  const selected = buckets.find((bucket) => bucket.start === pickedStart) ?? buckets.at(-1) ?? null;
  const max = Math.max(0.01, ...buckets.map((bucket) => filtered(bucket.costUsd, filter)));
  const total = filtered(timeline.totals.costUsd, filter);
  const totalTokens = filtered(timeline.totals.tokens, filter);
  const perMillion = filteredNullable(timeline.totals.costPerMillionTokensUsd, filter);
  const finished = buckets.filter((bucket) => !bucket.partial);
  const averageFinished = finished.length
    ? finished.reduce((sum, bucket) => sum + filtered(bucket.costUsd, filter), 0) / finished.length
    : null;

  return (
    <section className="usage-panel usage-expense-panel" aria-labelledby="usage-expense-title">
      <div className="usage-panel-head">
        <div>
          <span className="usage-eyebrow">Expense history · {toolLabel(filter)}</span>
          <h3 id="usage-expense-title">What we have been paying</h3>
        </div>
        <div className="usage-expense-controls">
          <div className="usage-legend" aria-label="Chart legend">
            {(filter === "all" || filter === "claude") && <span><i className="claude" />Claude Code</span>}
            {(filter === "all" || filter === "codex") && <span><i className="codex" />Codex</span>}
          </div>
          <div className="mfilters" role="group" aria-label="Timeline granularity">
            {available.map((value) => (
              <button
                className={`mfilter${timeline.granularity === value ? " on" : ""}`}
                onClick={() => setGranularity(value)}
                key={value}
                aria-pressed={timeline.granularity === value}
              >
                {timelines[value]!.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {buckets.length === 0 ? (
        <p className="usage-expense-empty">
          Expense history starts from the first usage refresh. Nothing has been recorded yet.
        </p>
      ) : (
        <>
          <div className="usage-expense-totals">
            <div><span className="usage-eyebrow">Recorded spend</span><strong>{usdPrecise(total)}</strong><small>since {shortWeek(timeline.coverageStart ?? buckets[0]!.start)} · {timeline.totals.days} day{timeline.totals.days === 1 ? "" : "s"}</small></div>
            <div><span className="usage-eyebrow">Per {granularity === "week" ? "full week" : "full month"}</span><strong>{averageFinished === null ? "—" : usdPrecise(averageFinished)}</strong><small>{finished.length} completed period{finished.length === 1 ? "" : "s"}</small></div>
            <div><span className="usage-eyebrow">Cost per 1M tokens</span><strong>{perMillion === null ? "—" : usdPrecise(perMillion)}</strong><small>{tokens(totalTokens)} tokens · {timeline.observedDays} active day{timeline.observedDays === 1 ? "" : "s"}</small></div>
          </div>

          <div className="usage-chart usage-expense-chart" role="img" aria-label={`${timeline.label} subscription spend for ${toolLabel(filter)}`}>
            {buckets.map((bucket) => {
              const cost = filtered(bucket.costUsd, filter);
              const height = Math.max(2, (cost / max) * 100);
              const claudeShare = bucket.costUsd.total ? (bucket.costUsd.claude / bucket.costUsd.total) * 100 : 0;
              const rate = filteredNullable(bucket.costPerMillionTokensUsd, filter);
              const isSelected = selected?.start === bucket.start;
              return (
                <button
                  type="button"
                  className={`usage-week${bucket.current ? " current" : ""}${bucket.partial ? " partial" : ""}${isSelected ? " selected" : ""}`}
                  key={bucket.start}
                  aria-pressed={isSelected}
                  onClick={() => setPicked(`${timeline.granularity}:${bucket.start}`)}
                  title={`${bucket.start} to ${bucket.end} · ${usdPrecise(cost)} over ${bucket.days} day${bucket.days === 1 ? "" : "s"} · ${tokens(filtered(bucket.tokens, filter))} tokens${rate === null ? "" : ` · ${usdPrecise(rate)} per 1M tokens`}${bucket.partial ? " · still running" : ""}`}
                >
                  <span className="usage-week-total">{cost ? usdPrecise(cost) : "—"}</span>
                  <div className="usage-bar-rail">
                    <div className="usage-bar" style={{ height: `${height}%` }}>
                      {(filter === "all" || filter === "claude") && <span className="usage-bar-claude" style={{ height: `${filter === "claude" ? 100 : claudeShare}%` }} />}
                      {(filter === "all" || filter === "codex") && <span className="usage-bar-codex" style={{ height: `${filter === "codex" ? 100 : 100 - claudeShare}%` }} />}
                    </div>
                  </div>
                  <span className="usage-expense-rate">{rate === null ? "no tokens" : `${usdPrecise(rate)}/M`}</span>
                  <span className="usage-week-label">{bucket.label}{bucket.partial ? " ·" : ""}</span>
                </button>
              );
            })}
          </div>
          {selected && <BucketBreakdown bucket={selected} filter={filter} projectName={projectName} />}

          <p className="usage-expense-note">
            Subscription fees are fixed, so a period costs its share of the monthly fee whether or not anyone worked.
            A period marked · is still running and its cost is still growing. Each bar carries the price paid per million
            tokens produced in that period, which is where the change actually shows. Select any bar to see which projects
            that period's money went to.
          </p>
        </>
      )}
    </section>
  );
}

export default function AdminUsagePage({ onClose, onOpenUsers, onOpenErrors }: Props) {
  const [report, setReport] = useState<AiUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ToolFilter>("all");

  const refresh = () => {
    setError(null);
    getAiUsage().then(setReport).catch((e) => setError((e as Error).message));
  };

  useEffect(() => { refresh(); }, []);

  const projects = useMemo(() => {
    if (!report) return [];
    return report.projects.filter((project) => filtered(project.currentWeek, filter) > 0
      || filtered(project.last30Days, filter) > 0)
      .sort((a, b) => filtered(b.currentWeek, filter) - filtered(a.currentWeek, filter)
      || filtered(b.last30Days, filter) - filtered(a.last30Days, filter)
      || a.name.localeCompare(b.name));
  }, [report, filter]);

  // History stores project ids only, so names are resolved against the current
  // report: a project renamed since is read back under the name it has now,
  // rather than one frozen at the moment the tokens were recorded.
  const projectName = useMemo(() => {
    const names = new Map((report?.projects ?? []).map((project) => [project.id, project.name]));
    return (id: string) => names.get(id) ?? id;
  }, [report]);

  const subscriptions = report?.subscriptions.filter((subscription) => filter === "all"
    || (filter === "claude" ? subscription.id === "claude-code" : subscription.id === "chatgpt")) ?? [];
  const selectedCost = subscriptions.reduce((sum, subscription) => sum + subscription.monthlyCostUsd, 0);
  const selectedSeats = subscriptions.reduce((sum, subscription) => sum + subscription.seats, 0);
  const observedDaysByTool = report?.forecast.observedDaysByTool ?? {
    claude: report?.forecast.observedDays ?? 1,
    codex: report?.forecast.observedDays ?? 1,
    total: report?.forecast.observedDays ?? 1,
  };
  const confidenceByTool = report?.forecast.confidenceByTool ?? {
    claude: report?.forecast.confidence ?? "low",
    codex: report?.forecast.confidence ?? "low",
    total: report?.forecast.confidence ?? "low",
  };
  const observedDays = filtered(observedDaysByTool, filter);
  const forecastConfidence = report
    ? (filter === "all" ? confidenceByTool.total : confidenceByTool[filter])
    : "low";
  const sourcesComplete = subscriptions.length > 0 && subscriptions.every((subscription) => subscription.connected && subscription.complete);
  const displayedForecastConfidence = sourcesComplete ? forecastConfidence : "low";
  const measuredDaysLabel = report && filter === "all"
    ? (() => {
        const values = [observedDaysByTool.claude, observedDaysByTool.codex];
        const min = Math.min(...values);
        const max = Math.max(...values);
        return min === max ? `${max} days measured` : `${min}–${max} days measured`;
      })()
    : `${observedDays} days measured`;
  const savePlanSetting = (subscriptionId: UsageSubscription["id"], setting: UsagePlanSetting) => {
    setReport((current) => current ? {
      ...current,
      subscriptions: current.subscriptions.map((subscription) => subscription.id === subscriptionId
        ? { ...subscription, seats: setting.planCount, weeklyUtilization: setting }
        : subscription),
    } : current);
  };

  return (
    <div className="members members-screen usage-screen">
      <div className="members-head usage-head">
        <div className="members-head-inner">
          <div className="members-title-row">
            <div className="members-title">
              <h2>AI usage</h2>
              <p>Subscription activity by project, measured directly from Claude Code and Codex sessions.</p>
            </div>
            <div className="members-head-actions">
              <button className="btn btn-ghost" onClick={onClose}><IconBack /> Back to chat</button>
            </div>
          </div>
          <div className="admin-tabs" role="tablist" aria-label="Workspace administration">
            <button role="tab" aria-selected="false" onClick={onOpenUsers}>Users</button>
            <button role="tab" aria-selected="true" className="on">Usage</button>
            <button role="tab" aria-selected="false" onClick={onOpenErrors}>Errors</button>
          </div>
        </div>
      </div>

      <div className="usage-body">
        {error && (
          <div className="members-alert" role="alert">
            <span>Could not load usage: {error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss"><IconX /></button>
            <button className="usage-retry" onClick={refresh}>Retry</button>
          </div>
        )}

        {!report ? (
          <div className="usage-loading" aria-live="polite">Reading coding activity…</div>
        ) : (
          <>
            <div className="usage-scope" aria-label="Dashboard usage scope">
              <div><span className="usage-eyebrow">Dashboard view</span><strong>{toolLabel(filter)}</strong></div>
              <div className="mfilters" role="group" aria-label="Filter the complete dashboard by coding tool">
                {(["all", "claude", "codex"] as const).map((value) => (
                  <button className={`mfilter${filter === value ? " on" : ""}`} onClick={() => setFilter(value)} key={value} aria-pressed={filter === value}>
                    {toolLabel(value)}
                  </button>
                ))}
              </div>
            </div>

            <div className="usage-summary" aria-label="Usage overview">
              <article className="usage-kpi cost">
                <span className="usage-eyebrow">{toolLabel(filter)} subscriptions</span>
                <strong>{usd(selectedCost)}</strong>
                <small>per month · {selectedSeats} seat{selectedSeats === 1 ? "" : "s"}</small>
              </article>
              <article className="usage-kpi">
                <span className="usage-eyebrow">This week</span>
                <strong>{tokens(filtered(report.summary.currentWeek, filter))}</strong>
                <small>tokens · {tokens(filtered(report.summary.currentWeekPace, filter))} projected pace</small>
                <Trend value={filteredNullable(report.summary.weeklyTrend, filter)} />
              </article>
              <article className="usage-kpi">
                <span className="usage-eyebrow">30-day benchmark</span>
                <strong>{tokens(filtered(report.benchmark.tokens, filter))}</strong>
                <small>{tokens(Math.round(filtered(report.benchmark.tokens, filter) / observedDays))} {toolLabel(filter)} tokens / observed day</small>
              </article>
              <article className="usage-kpi forecast">
                <span className="usage-eyebrow">{monthName(report.forecast.month)} estimate</span>
                <strong>{tokens(filtered(report.forecast.tokens, filter))}</strong>
                <small>{displayedForecastConfidence} confidence · {measuredDaysLabel}{sourcesComplete ? "" : " · source coverage incomplete"}</small>
              </article>
            </div>

            <div className="usage-main-grid">
              <UsageTrendChart report={report} filter={filter} />
              <section className="usage-subscriptions" aria-label="Subscriptions">
                {subscriptions.map((subscription) => (
                  <SubscriptionCard subscription={subscription} key={subscription.id} onSaved={(setting) => savePlanSetting(subscription.id, setting)} />
                ))}
              </section>
            </div>

            {report.timeline && <UsageExpenseTimeline timelines={report.timeline} filter={filter} projectName={projectName} />}

            <section className="usage-panel usage-projects" aria-labelledby="usage-projects-title">
              <div className="usage-panel-head usage-project-head">
                <div>
                  <span className="usage-eyebrow">Project allocation</span>
                  <h3 id="usage-projects-title">Where {toolLabel(filter).toLowerCase()} is working</h3>
                </div>
              </div>
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead><tr><th>Project</th><th>This week</th><th>30-day benchmark</th><th>Next month</th><th>Share / allocation</th><th>Weekly pace</th></tr></thead>
                  <tbody>
                    {projects.map((project) => {
                      const current = filtered(project.currentWeek, filter);
                      const month = filtered(project.last30Days, filter);
                      const forecast = filtered(project.projectedNextMonth, filter);
                      const sessions = filtered(project.currentWeekSessionsByTool, filter);
                      const monthSessions = filtered(project.last30DaysSessionsByTool, filter);
                      const pace = filtered(project.weeklyPace, filter);
                      const trend = filteredNullable(project.weeklyTrend, filter);
                      const shareByTool = project.sharePercentByTool ?? {
                        claude: report.benchmark.tokens.claude ? (project.last30Days.claude / report.benchmark.tokens.claude) * 100 : 0,
                        codex: report.benchmark.tokens.codex ? (project.last30Days.codex / report.benchmark.tokens.codex) * 100 : 0,
                        total: project.sharePercent,
                      };
                      const share = filtered(shareByTool, filter);
                      const allocation = project.estimatedMonthlyAllocationUsdByTool
                        ? filtered(project.estimatedMonthlyAllocationUsdByTool, filter)
                        : filter === "all" ? project.estimatedMonthlyAllocationUsd : (share / 100) * selectedCost;
                      const lastActivity = filter === "all" ? project.lastActivityAt : project.lastActivityAtByTool[filter];
                      const groupLabel = project.groupType === "workspace_project" ? "Workspace project" : project.groupType === "ai_agent" ? "AI agent / service" : "Local activity";
                      return (
                        <tr key={project.id}>
                          <td>
                            <div className="usage-project-name"><span className={`usage-project-glyph${project.groupType === "workspace_project" ? "" : " local"}`}>{project.name.slice(0, 2).toUpperCase()}</span><span><b>{project.name}</b><small>{groupLabel} · {ago(lastActivity)}</small></span></div>
                          </td>
                          <td><b className="usage-number">{tokens(current)}</b><small>{sessions} session{sessions === 1 ? "" : "s"} this week</small></td>
                          <td><b className="usage-number">{tokens(month)}</b>{filter === "all" ? <div className="usage-split"><span className="claude">{tokens(project.last30Days.claude)}</span><span className="codex">{tokens(project.last30Days.codex)}</span></div> : <small>{monthSessions} {toolLabel(filter)} session{monthSessions === 1 ? "" : "s"}</small>}</td>
                          <td><b className="usage-number">{tokens(forecast)}</b><small>{month ? "recent daily pace" : "no baseline"}</small></td>
                          <td><b className="usage-number">{share.toFixed(1)}%</b><small>{usd(allocation)} · 30-day allocation</small></td>
                          <td><b className="usage-number">{tokens(pace)}</b><Trend value={trend} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="usage-method">
              <span className="usage-method-mark">i</span>
              <p><strong>How to read this:</strong> usage is measured from local CLI token records, then attributed by Fundai conversation project, repository, or recognized AI agent. The {toolLabel(filter)} view scopes every number on this page. Weekly plan percentages refresh automatically from each connected Claude or Codex account; admin values are used only as fallbacks for plans without a current source. Subscription fees are fixed; allocations divide the selected fee by 30-day token share and are planning estimates, not vendor invoices.</p>
              <time dateTime={report.generatedAt}>Updated {ago(report.generatedAt)}</time>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
